import { PROFILE_SCOPE_ID_RE, variantScopeId, recording, referenceRows, validateFile, mapProposalStatus, treeFromRegistry, reportFromProposal, amendment, permissions, flattenTree } from './variants-model.js';
import { createVariantsFixtures } from './variants-fixtures.js';

export function createVariantsLive({ api, seat, clientName, FormDataClass = FormData }) {
  const local = new Map();
  const rows = new Map();
  let seq = 0;
  const get = id => { const row = local.get(id) || rows.get(id); if (!row) throw new Error('Variant not found.'); return row; };
  const requireStart = () => { if (!seat().start) throw new Error('You do not have permission to start a variant.'); };
  const update = (row, raw) => { row.raw = { ...row.raw, ...raw }; row.request_id = raw.request_id || row.request_id; row.status = mapProposalStatus(row.raw); row.report = reportFromProposal(row.raw); return row; };
  return {
    live: true,
    async getTree() {
      const registry = await api.sabreProfiles();
      const queue = seat().decide ? await api.sabreProfileApprovalQueue() : { proposals: [] };
      rows.clear();
      const activeIds = new Set((registry.profiles || []).map(p => p.profile_id));
      for (const p of registry.profiles || []) rows.set(p.profile_id, { id: p.profile_id, name: p.scope?.variant_id || p.profile_id, status: 'ready', raw: p });
      for (const p of queue.proposals || []) {
        if (activeIds.has(p.profile_id) && p.status === 'approved') continue;
        const known = [...local.values()].find(r => r.request_id === p.request_id);
        if (known) update(known, p);
        else rows.set(p.request_id, update({ id: p.request_id, name: p.scope?.variant_id || p.profile_id || p.request_id }, p));
      }
      for (const row of local.values()) {
        if (row.status === 'ready' && activeIds.has(row.raw?.profile_id)) rows.delete(row.raw.profile_id);
        rows.set(row.id, row);
      }
      return treeFromRegistry([...rows.values()], clientName);
    },
    async createDraft({ client_id, sub_brand_id, variant_id, name, notes }) {
      requireStart();
      if (name !== undefined) variant_id = variantScopeId(name);
      if (![client_id, sub_brand_id, variant_id].every(x => PROFILE_SCOPE_ID_RE.test(x || ''))) throw new Error('IDs must start with a letter or number and contain only letters, numbers, . _ : - (up to 128 characters).');
      const row = { id: `local:${++seq}`, name: name?.trim() || variant_id, client_id, sub_brand_id, variant_id, notes, status: 'draft', files: [], registered: false, local: true };
      local.set(row.id, row); return { ...row };
    },
    async getFiles(id) { return (get(id).files || []).map(({ file, ...meta }) => meta); },
    async stageFile(id, meta, file) {
      requireStart(); const row = get(id);
      if (!row.local || !['draft', 'files_staged', 'harvest_failed'].includes(row.status)) throw new Error('This upload set is read-only.');
      if (meta.kind === 'notes' || (meta.email || 0) !== 0) throw new Error('needs server support — VAR-04 G1');
      validateFile(row.files, meta.kind, file);
      row.files.push({ ...meta, name: file.name, bytes: file.size, file }); row.status = 'files_staged';
    },
    async removeFile(id, name) {
      requireStart(); const row = get(id);
      if (!row.local || !['draft', 'files_staged', 'harvest_failed'].includes(row.status)) throw new Error('This upload set is read-only.');
      row.files = row.files.filter(f => f.name !== name); row.status = row.files.length ? 'files_staged' : 'draft';
    },
    async startHarvest(id) {
      requireStart(); const row = get(id);
      if (!row.local || !recording(row.files).ok || row.status === 'harvesting') throw new Error('Stage exactly one shipped HTML and one design PDF first.');
      const fd = new FormDataClass(); fd.append('variant_id', row.variant_id); fd.append('sub_brand_id', row.sub_brand_id);
      row.files.filter(f => f.kind !== 'notes').forEach(f => fd.append('files', f.file, f.name));
      row.status = 'harvesting'; row.error = null;
      try {
        // GooseAPI owns the server operation polling. Its promise resolves only at terminal state.
        const result = await api.proposeSabreProfile(fd);
        if (!result?.request_id) throw new Error('The server did not return a proposal ID.');
        update(row, result);
        return { job_id: row.request_id, row, report: row.report };
      } catch (error) {
        if (error.data?.request_id) update(row, error.data);
        row.status = 'harvest_failed'; row.error = errorText(error); throw error;
      }
    },
    async inspect(id) {
      const row = get(id);
      if (row.request_id) update(row, await api.sabreProfileProposal(row.request_id));
      return { ...row, report: row.report || null };
    },
    async putProfile(id, { checklist, summary }) {
      const row = get(id);
      if (!permissions(row, seat()).edit) throw new Error('Only the proposer can edit an unsubmitted draft.');
      update(row, await api.amendSabreProfile(row.request_id, amendment(row.raw.candidate_profile, checklist, summary)));
      return row;
    },
    async finalize(id, action = 'submit') {
      const row = get(id); const allow = permissions(row, seat());
      if (action === 'submit') {
        if (!allow.submit) throw new Error('Only the proposer can submit this draft.');
        update(row, await api.submitSabreProfile(row.request_id));
      } else {
        if (!allow.decide || !['approve', 'reject'].includes(action)) throw new Error('A different reviewer must decide this proposal.');
        const result = await api.reviewSabreProfile(row.request_id, action);
        update(row, { ...result, review: { action } });
      }
      return row;
    },
  };
}
export function errorText(error) {
  const detail = error?.data?.error;
  return (typeof detail === 'string' ? detail : detail?.message) || error?.message || 'Request failed.';
}

export function createVariantsMock(seat) {
  const fixture = createVariantsFixtures();
  const seeded = flattenTree(fixture.tree);
  const sample = seeded.find(v => v.profile_v1).profile_v1;
  let seq = 0;
  const proposals = new Map();
  for (const row of seeded) {
    row.local = ['draft', 'files_staged', 'harvest_failed'].includes(row.status);
    if (['review', 'ready'].includes(row.status)) {
      const candidate = row.profile_v1 || fixture.reports[row.id]?.proposed_profile;
      const raw = { request_id: row.id, status: 'ready_for_review', workflow_status: row.status === 'ready' ? 'submitted' : 'draft', requested_by: { user_id: seat().userId }, candidate_profile: candidate, scope: candidate?.scope, decision_rows: candidate?.region_policies || [], terminal_findings: [], container_width: row.profile?.viewport_standards?.desktop_px };
      if (row.status === 'ready') raw.review = { action: 'approve' };
      proposals.set(row.id, raw);
    }
  }
  const api = createVariantsLive({
    seat, clientName: id => fixture.tree.find(c => c.slug === id)?.client || id,
    api: {
      sabreProfiles: async () => ({ profiles: [] }), sabreProfileApprovalQueue: async () => ({ proposals: [] }),
      proposeSabreProfile: async () => {
        await new Promise(r => setTimeout(r, 500));
        const request_id = `mock:${++seq}`;
        const raw = { ...structuredClone(proposals.values().next().value), request_id, review: null, workflow_status: 'draft', requested_by: { user_id: seat().userId } };
        proposals.set(request_id, raw); return raw;
      },
      sabreProfileProposal: async id => proposals.get(id),
      amendSabreProfile: async (id, body) => { const raw = proposals.get(id); raw.revision = { resolved_finding_ids: body.resolved_finding_ids }; return raw; },
      submitSabreProfile: async id => { const raw = proposals.get(id); raw.workflow_status = 'submitted'; return raw; },
      reviewSabreProfile: async (id, action) => { const raw = proposals.get(id); raw.review = { action }; return raw; },
    },
  });
  const editableSeed = id => {
    const row = seeded.find(r => r.id === id);
    if (!seat().start || !row || ['harvesting', 'archived', 'rejected'].includes(row.status)) throw new Error('This upload set is read-only.');
    return row;
  };
  return {
    ...api, live: false,
    async createDraft({ client_id, client, sub_brand, name, notes }) {
      if (!seat().start) throw new Error('You do not have permission to start a variant.');
      if (!client?.trim() || !name?.trim()) throw new Error('Client and variant name are required.');
      const row = { id: `fixture-draft:${++seq}`, name: name.trim(), client_id, client, sub_brand, sub_brand_id: sub_brand || '_root', notes, status: 'draft', registered: false, local: true };
      seeded.push(row); fixture.files[row.id] = []; return { ...row };
    },
    async getTree() {
      const local = flattenTree(await api.getTree());
      const tree = treeFromRegistry([...seeded, ...local], id => fixture.tree.find(c => c.slug === id)?.client || id);
      for (const client of tree) for (const sub of client.subs) for (const row of sub.variants) row.registered = seeded.find(s => s.id === row.id)?.registered === true;
      return tree;
    },
    async getFiles(id) { return seeded.some(r => r.id === id) ? (fixture.files[id] || []) : api.getFiles(id); },
    async stageFile(id, meta, file) {
      if (!seeded.some(r => r.id === id)) return api.stageFile(id, meta, file);
      const row = editableSeed(id); const files = fixture.files[id] ||= [];
      validateFile(files.filter(f => (f.email || 0) === (meta.email || 0)), meta.kind, file, { fixture: true });
      files.push({ ...meta, name: file.name, bytes: file.size, file });
      if (row.status === 'draft') row.status = 'files_staged';
    },
    async removeFile(id, name, email = 0) {
      if (!seeded.some(r => r.id === id)) return api.removeFile(id, name);
      const row = editableSeed(id);
      fixture.files[id] = (fixture.files[id] || []).filter(f => f.name !== name || (f.email || 0) !== email);
      if (['draft', 'files_staged'].includes(row.status)) row.status = fixture.files[id].length ? 'files_staged' : 'draft';
    },
    async startHarvest(id) {
      if (!seeded.some(r => r.id === id)) return api.startHarvest(id);
      const row = editableSeed(id);
      if (!referenceRows(fixture.files[id] || []).ok) throw new Error('One reference email needs shipped HTML and three of its four kinds.');
      row.status = 'harvesting'; row.error = null;
      await new Promise(resolve => setTimeout(resolve, 500));
      fixture.reports[id] ||= structuredClone(fixture.reports.v_syf_cc_promo);
      const candidate = fixture.reports[id].proposed_profile || sample;
      proposals.set(id, { request_id: id, status: 'ready_for_review', workflow_status: 'draft', requested_by: { user_id: seat().userId }, candidate_profile: candidate, decision_rows: candidate.region_policies, terminal_findings: [] });
      row.status = 'review'; row.request_id = id;
      return { job_id: id };
    },
    async inspect(id) {
      const row = seeded.find(r => r.id === id);
      if (!row) return api.inspect(id);
      const raw = proposals.get(id);
      const report = raw?.candidate_profile ? structuredClone(fixture.reports[id] || reportFromProposal(raw)) : row.profile ? {
        proposed_profile: null, patterns: [], esp_tags: [], gold_candidates: [],
        checklist_seed: structuredClone(row.profile.checklist || []), viewport_hint: { ...row.profile.viewport_standards }, esp_hint: structuredClone(row.profile.esp_matrix || []),
      } : null;
      if (report) {
        report.checklist_seed = report.checklist_seed.map(c => ({ ...c, accepted: c.accepted ?? true }));
        if (!fixture.reports[id]) report.gold_candidates = [];
      }
      return { ...row, raw: raw || {}, report };
    },
    async putProfile(id, body) {
      if (!proposals.has(id) || !seeded.some(r => r.id === id)) return api.putProfile(id, body);
      const row = seeded.find(r => r.id === id), raw = proposals.get(id);
      if (!seat().start || !['review', 'ready'].includes(row.status)) throw new Error('This fixture profile is read-only.');
      raw.revision = { resolved_finding_ids: body.checklist.filter(c => c.finding && c.accepted).map(c => c.id) };
      row.profile = structuredClone(body);
    },
    async finalize(id, action) {
      const row = seeded.find(r => r.id === id); if (!row) return api.finalize(id, action);
      const raw = proposals.get(id);
      if (action === 'record') {
        if (!seat().start || !['review', 'ready'].includes(row.status)) throw new Error('This fixture profile is read-only.');
        if (!row.profile?.checklist?.some(c => c.accepted)) throw new Error('Checklist must have at least one accepted item.');
        if (!(row.profile?.viewport_standards?.desktop_px > 0)) throw new Error('Viewport standards are incomplete.');
        row.status = 'ready'; raw.review = { action: 'approve' }; raw.workflow_status = 'submitted';
        return row;
      }
      const allow = permissions({ ...row, raw }, seat());
      if (action === 'submit' && allow.submit) raw.workflow_status = 'submitted';
      else if (['approve', 'reject'].includes(action) && allow.decide) { raw.review = { action }; row.status = action === 'approve' ? 'ready' : 'rejected'; }
      else throw new Error('A different reviewer must decide this proposal.');
    },
  };
}
